import React, { useState } from "react";
import { apiFetch } from "../services/api";
import LoadingButton from "./common/LoadingButton";

const ChangePassword = ({ user, onBack, onSuccess, variant = "fullscreen" }) => {
  const isEmbedded = variant === "embedded";
  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    retypePassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    // Clear messages when user starts typing
    if (error) setError("");
    if (success) setSuccess("");
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return "Password must be at least 8 characters long";
    }
    if (password.length > 72) {
      return "Password must be no more than 72 characters long";
    }

    // Strong password policy: must contain at least 3 of the following:
    // - Uppercase letter
    // - Lowercase letter
    // - Number
    // - Special character
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasDigit = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/.test(password);

    const strengthCount = [hasUpper, hasLower, hasDigit, hasSpecial].filter(
      Boolean
    ).length;
    if (strengthCount < 3) {
      return "Password must contain at least 3 of the following: uppercase letter, lowercase letter, number, or special character";
    }

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate passwords match
    if (formData.newPassword !== formData.retypePassword) {
      setError("New passwords do not match");
      setLoading(false);
      return;
    }

    // Validate password strength
    const passwordError = validatePassword(formData.newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    // Validate current password is provided
    if (!formData.currentPassword) {
      setError("Please enter your current password");
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch("/api/auth/change-password", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          current_password: formData.currentPassword,
          new_password: formData.newPassword,
          retype_password: formData.retypePassword,
        }),
      });

      if (response.ok) {
        setSuccess("Password changed successfully!");
        // Clear form
        setFormData({
          currentPassword: "",
          newPassword: "",
          retypePassword: "",
        });
        // Navigate to landing page after showing success message (2 seconds)
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 2000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        let message = "Failed to change password";

        // Handle error response structure (similar to Signup.js)
        if (errorData?.error) {
          // Error wrapped by main.py exception handler
          if (typeof errorData.error === "string") {
            message = errorData.error;
          } else if (typeof errorData.error === "object") {
            if (errorData.error.message) {
              message = errorData.error.message;
            } else if (errorData.error.detail) {
              message = errorData.error.detail;
            }
          }
        } else if (errorData?.detail) {
          if (typeof errorData.detail === "string") {
            message = errorData.detail;
          } else if (typeof errorData.detail === "object") {
            // New format: detail is an object with message property
            if (errorData.detail.message) {
              message = errorData.detail.message;
            } else if (errorData.detail.detail) {
              message = errorData.detail.detail;
            }
          }
        } else if (errorData?.message) {
          message = errorData.message;
        }

        setError(message);
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputClass = isEmbedded
    ? "block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-cyan-500 focus:outline-none focus:ring-1 focus:ring-cyan-500 sm:text-sm"
    : "appearance-none relative block w-full px-3 py-2 border border-gray-600 placeholder-gray-400 text-white bg-gray-800 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm";

  const labelClass = isEmbedded
    ? "mb-1 block text-sm font-medium text-slate-700"
    : "block text-sm font-medium text-gray-300 mb-1";

  return (
    <div
      className={
        isEmbedded
          ? ""
          : "flex min-h-screen items-center justify-center bg-gray-900 px-4 py-12 sm:px-6 lg:px-8"
      }
    >
      <div className={isEmbedded ? "w-full max-w-xl space-y-4" : "w-full max-w-md space-y-8"}>
        {!isEmbedded ? (
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">Change Password</h2>
            <p className="mt-2 text-center text-sm text-gray-400">Update your account password</p>
          </div>
        ) : null}
        <form className={isEmbedded ? "space-y-4" : "mt-8 space-y-6"} onSubmit={handleSubmit}>
          <div className={isEmbedded ? "space-y-4" : "space-y-4 rounded-md shadow-sm"}>
            <div>
              <label htmlFor="currentPassword" className={labelClass}>
                Current Password
              </label>
              <input
                id="currentPassword"
                name="currentPassword"
                type="password"
                autoComplete="current-password"
                required
                maxLength={72}
                className={inputClass}
                placeholder="Enter your current password"
                value={formData.currentPassword}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="newPassword" className={labelClass}>
                New Password
              </label>
              <input
                id="newPassword"
                name="newPassword"
                type="password"
                autoComplete="new-password"
                required
                maxLength={72}
                className={inputClass}
                placeholder="Enter your new password (min 8 characters)"
                value={formData.newPassword}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="retypePassword" className={labelClass}>
                Confirm New Password
              </label>
              <input
                id="retypePassword"
                name="retypePassword"
                type="password"
                autoComplete="new-password"
                required
                maxLength={72}
                className={inputClass}
                placeholder="Confirm your new password"
                value={formData.retypePassword}
                onChange={handleChange}
              />
            </div>
          </div>

          {error && (
            <div
              className={
                isEmbedded
                  ? "rounded-lg border border-red-200 bg-red-50 p-3"
                  : "rounded-md bg-red-900/20 p-4"
              }
            >
              <div className={`text-sm ${isEmbedded ? "text-red-800" : "text-red-300"}`}>
                {error}
              </div>
            </div>
          )}

          {success && (
            <div
              className={
                isEmbedded
                  ? "rounded-lg border border-emerald-200 bg-emerald-50 p-3"
                  : "rounded-md bg-green-900/20 p-4"
              }
            >
              <div className={`text-sm ${isEmbedded ? "text-emerald-900" : "text-green-300"}`}>
                {success}
              </div>
            </div>
          )}

          {onBack ? (
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={onBack}
                className={
                  isEmbedded
                    ? "text-sm font-medium text-slate-600 hover:text-slate-900"
                    : "text-sm font-medium text-gray-400 hover:text-gray-300"
                }
              >
                ← Back
              </button>
            </div>
          ) : null}

          <div>
            <LoadingButton
              type="submit"
              loading={loading}
              loadingLabel="Changing password..."
              className={
                isEmbedded
                  ? "flex w-full justify-center rounded-lg border border-transparent bg-cyan-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  : "group relative flex w-full justify-center rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              Change Password
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ChangePassword;
